import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Tabs, Tab, Drawer, List, Divider, ListItem, ListItemButton, ListItemIcon, ListItemText, TextField, Button, MenuItem, CircularProgress, Alert, Card, CardContent, Grid, Switch, FormControlLabel, Select, InputLabel, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel as MuiInputLabel, IconButton, Chip, RadioGroup, Radio } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  Dashboard as DashboardIcon,
  School as SchoolIcon,
  Forum as ForumIcon,
  People as PeopleIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  Mail,
  BarChart,
  Cancel,
  CloudUpload,
  Delete,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { IHost, WorkshopApi } from '../../../api/workshop-api';
import { Avatar } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { WorkshopAdminApi } from '../../../api/workshop-admin-api';
import DttTableData from '../../../components/DttTableData';
import TextEditor from '../../../components/TextEditor';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useAlert } from '@/contexts/AlertContext';
import useDebounce from '@/hooks/use-debounce';
import { apiRemote } from '@/api';

const drawerWidth = 250;

// Workshop status constants
const WORKSHOP_STATUS_DRAFT = 'draft';
const WORKSHOP_STATUS_PUBLISHED = 'published';
const WORKSHOP_STATUS_FULL = 'full';
const WORKSHOP_STATUS_COMPLETED = 'completed';
const WORKSHOP_STATUS_IN_PROGRESS = 'in_progress';
const WORKSHOP_STATUS_CANCELLED = 'cancelled';

const tabLabels = [
  'Info & Edit',
  'Attendees',
  'Bulk Email',
  'Analytics',
  'Cancel Workshop',
];

const navigationItems = [
  { id: "dashboard", label: "Dashboard", icon: <DashboardIcon /> },
  { id: "workshops", label: "Workshops", icon: <SchoolIcon /> },
  { id: "community", label: "Community", icon: <ForumIcon /> },
  { id: "users", label: "Users", icon: <PeopleIcon /> },
  { id: "emails", label: "Email Template", icon: <Mail /> },
  { id: "analytics", label: "Analytics", icon: <AnalyticsIcon /> },
  { id: "settings", label: "Settings", icon: <SettingsIcon /> },
];

// Helper function to calculate workshop status based on date and duration
const calculateWorkshopStatus = (workshop: any): string => {
  if (workshop.status === WORKSHOP_STATUS_CANCELLED) {
    return WORKSHOP_STATUS_CANCELLED;
  }
  
  if (workshop.status === WORKSHOP_STATUS_DRAFT) {
    return WORKSHOP_STATUS_DRAFT;
  }

  const now = new Date();
  const workshopDate = new Date(workshop.date);
  const endTime = new Date(workshopDate.getTime() + (workshop.duration || 0) * 60 * 1000);

  if (now < workshopDate) {
    return workshop.status || WORKSHOP_STATUS_PUBLISHED;
  } else if (now >= workshopDate && now <= endTime) {
    return WORKSHOP_STATUS_IN_PROGRESS;
  } else {
    return WORKSHOP_STATUS_COMPLETED;
  }
};

// Helper function to check if tab should be disabled
const isTabDisabled = (tabIndex: number, workshopStatus: string): boolean => {
  switch (tabIndex) {
    case 0: // Info & Edit - always enabled
      return false;
    case 1: // Attendees - disabled for draft
      return workshopStatus === WORKSHOP_STATUS_DRAFT;
    case 2: // Bulk Email - disabled for draft and cancelled
      return workshopStatus === WORKSHOP_STATUS_DRAFT || workshopStatus === WORKSHOP_STATUS_CANCELLED;
    case 3: // Analytics - disabled for draft
      return workshopStatus === WORKSHOP_STATUS_DRAFT;
    case 4: // Cancel Workshop - disabled for cancelled and completed
      return workshopStatus === WORKSHOP_STATUS_CANCELLED || workshopStatus === WORKSHOP_STATUS_COMPLETED;
    default:
      return false;
  }
};

interface AttachedDocument {
  id: number;
  workshop_id: number;
  file_name: string;
  file_type: string;
  file_size: number;
  file_url: string;
  created_at: string;
  updated_at: string;
}

interface Topic {
  id: number;
  name: string;
}

interface Educator {
  ID: string; // Changed from 'id' to 'ID' to match your data
  user_login: string;
  user_pass: string;
  user_nicename: string;
  user_email: string;
  user_url: string;
  user_registered: string;
  user_activation_key: string;
  user_status: string;
  display_name: string; // This is the name you want to display
  status: string;
}

type FileOrAttachedDocument = File | AttachedDocument;

function WorkshopInfoEdit({ workshopId }: { workshopId: string | undefined }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [form, setForm] = useState<any>({});
  const [categories, setCategories] = useState<any[]>([]);
  const [formats] = useState([
    { value: 'online', label: 'Online' },
    { value: 'in-person', label: 'In-Person' },
    { value: 'hybrid', label: 'Hybrid' },
  ]);
  const alert = useAlert();
  const [topics, setTopics] = useState<any[]>([]);

  const [educators, setEducators] = useState<any[]>([]);
  const [searchEducators, setSearchEducators] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchEducators, 500);
  const [selectedEducator, setSelectedEducator] = useState<Educator | null>(
    null
  );
  const [selectedEducators, setSelectedEducators] = useState<Educator[]>([]);

  const statusOptions = [
    { value: WORKSHOP_STATUS_DRAFT, label: 'Draft' },
    { value: WORKSHOP_STATUS_PUBLISHED, label: 'Published' },
    { value: WORKSHOP_STATUS_COMPLETED, label: 'Completed' },
    { value: WORKSHOP_STATUS_CANCELLED, label: 'Cancelled' },
    { value: WORKSHOP_STATUS_IN_PROGRESS, label: 'In Progress' },
    { value: WORKSHOP_STATUS_FULL, label: 'Full' },
  ];

  useEffect(() => {
    if (!workshopId) return;
    setLoading(true);
    WorkshopAdminApi.adminGetWorkshopById(workshopId)
      .then((res: any) => {
        if (res && res.data) {
          const workshopData = res.data;
          // Calculate current status
          workshopData.calculatedStatus = calculateWorkshopStatus(workshopData);

          let parsedTopics: Topic[] = [];
          if (workshopData.topic && typeof workshopData.topic === "string") {
            try {
              // Parse chuỗi JSON thành mảng các đối tượng topic
              parsedTopics = JSON.parse(workshopData.topic);
            } catch (e) {
              console.error("Error parsing topic string:", e);
              alert.error("Failed to parse workshop topics.");
            }
          } else if (Array.isArray(workshopData.topic)) {
            // Nếu topic đã là một mảng (trường hợp lý tưởng), gán trực tiếp
            parsedTopics = workshopData.topic;
          }

          let parsedHosts: IHost[] = [];
          // Xử lý dữ liệu host: ưu tiên mảng hosts, nếu không có thì tạo từ các trường host cũ
          if (workshopData.hosts && workshopData.hosts !== "[]") {
              try {
                  parsedHosts = JSON.parse(workshopData.hosts);
              } catch (e) {
                  console.error("Error parsing hosts string:", e);
                  alert.error("Failed to parse workshop hosts.");
              }
          } else if (workshopData.host_name) {
              // Tương thích ngược: tạo mảng host từ các trường cũ
              parsedHosts = [{
                  host_name: workshopData.host_name,
                  host_email: workshopData.host_email,
                  host_organization: workshopData.host_organization,
                  host_bio: workshopData.host_bio_snippet,
              }];
          }

          setForm({
            ...workshopData,
            topic: parsedTopics,
            hosts: parsedHosts,
          });

          if (workshopData.host_type === "pythaverse" && parsedHosts.length > 0) {
            const educatorsToSelect = parsedHosts.map(host => ({
              ID: host.host_email,
              user_login: "",
              user_pass: "",
              user_nicename: "",
              user_email: host.host_email,
              user_url: "",
              user_registered: "",
              user_activation_key: "",
              user_status: "",
              display_name: host.host_name,
              status: "",
            }));
            setSelectedEducators(educatorsToSelect);
          }
        } else {
          setError('Workshop not found');
        }
      })
      .catch(() => setError('Failed to fetch workshop'))
      .finally(() => setLoading(false));
    WorkshopApi.getListCategory().then((data) => {
      setCategories(data.data || []);
    })
    .catch((error) => {
      console.error("Error fetching educators:", error);
      setCategories([]); // Clear educators on error
    });
  }, [workshopId]);

  useEffect(() => {
    if (searchEducators == "") {
      setEducators([]);
    } else {
      apiRemote
        .getListEducator(searchEducators)
        .then((data) => {
          setEducators(data.data);
        })
        .catch((error) => {
          console.error("Error fetching educators:", error);
          setEducators([]); // Clear educators on error
        });
    }
  }, [debouncedSearchTerm]);
  
  useEffect(() => {
    if (form.host_type === "pythaverse") {
        const newHosts: IHost[] = selectedEducators.map(edu => ({
            host_name: edu.display_name,
            host_email: edu.user_email,
            host_organization: "",
            host_bio: ""
        }));
        setForm((prev: any) => ({ ...prev, hosts: newHosts }));
    }
  }, [selectedEducators, form.host_type]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };
  const handleSwitch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.checked ? 1 : 0 });
  };
  const handleSelect = (e: any) => {
    const { value } = e.target;

    // Trạng thái được coi là "calculated" là In Progress và Completed
    const isCalculatedStatus = ['in_progress', 'completed'].includes(value);

    // Sử dụng callback function để cập nhật state an toàn
    setForm((prevForm: any) => {
      if (isCalculatedStatus) {
        // Nếu là calculated_status, status phải là 'published'
        return {
          ...prevForm,
          status: 'published',
          calculatedStatus: value,
        };
      } else {
        // Nếu là các trạng thái khác, cập nhật status và xóa calculatedStatus
        return {
          ...prevForm,
          status: value,
          calculatedStatus: null,
        };
      }
    });
    
  };

  const handleTopicsChange = (
    event: any,
    newValue: (string | { id: number; name: string })[]
  ) => {
    const formattedTopics = newValue.map((topic) =>
      typeof topic === "string" ? { id: Date.now(), name: topic } : topic
    );
    // Cập nhật formData.topics thay vì selectedTopics
    setForm((prev: any) => ({
      ...prev,
      topic: formattedTopics,
    }));
  };
  
  const handleCategory = (_: any, value: any) => {
    setForm({ ...form, category: value ? value.id : '' });
  };
  const handleAddGuest = () => {
    setForm((prev: any) => ({
      ...prev,
      hosts: [...prev.hosts, {
        host_name: "",
        host_email: "",
        host_organization: "",
        host_bio: ""
      }]
    }));
  };

  const handleRemoveGuest = (index: number) => {
    setForm((prev: any) => {
      const newHosts = [...prev.hosts];
      newHosts.splice(index, 1);
      return { ...prev, hosts: newHosts };
    });
  };
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      // Create a FormData object to send both text fields and files
      const formData = new FormData();

      // // Append all workshop data fields to formData
      for (const key in form) {
        // console.log(key, workshopData.hasOwnProperty(key), workshopData)
        if (form.hasOwnProperty(key)) {
          // Assert key as a valid key of workshopData to resolve TypeScript error
          const typedKey = key as keyof typeof form;
          
          // Handle specific cases for non-string data
          if (typedKey === "topic") {
            formData.append(typedKey, JSON.stringify(form[typedKey]));
          } else if (typedKey === "hosts" && Array.isArray(form[typedKey])) {
            // (workshopData[typedKey] as IHost[]).forEach((host, index) => {
            //   if (typeof host === "object") {
            //     Object.entries(host).forEach(([subKey, subValue]) => {
            //       if (subValue !== undefined && subValue !== null) {
            //         formData.append(
            //           `${typedKey}[${index}][${subKey}]`,
            //           subValue.toString()
            //         );
            //       }
            //     });
            //   }
            // });
            formData.append(typedKey, JSON.stringify(form[typedKey]));
          } else if (typedKey === "attached_documents") {
            // Separate existing documents (by URL) from new files
            const existingDocs = (form.attached_documents as FileOrAttachedDocument[]).filter(
              (doc): doc is AttachedDocument => !(doc instanceof File)
            );
            const newFiles = (form.attached_documents as FileOrAttachedDocument[]).filter(
              (doc): doc is File => doc instanceof File
            );

            // Append existing document URLs (or IDs if your API prefers)
            if (existingDocs.length > 0) {
              formData.append('existing_documents', JSON.stringify(existingDocs.map(doc => ({ id: doc.id, file_url: doc.file_url, file_name: doc.file_name, file_type: doc.file_type, file_size: doc.file_size }))));
            }

            // Append new files
            newFiles.forEach((file) => {
              formData.append('new_attached_documents[]', file); // Use array notation for multiple files
            });
          }
          else if (typedKey === "image_url" && form[typedKey] instanceof File) {
              formData.append(typedKey, form[typedKey]);
          }
          else if (form[typedKey] !== null && typeof form[typedKey] !== 'undefined') {
              formData.append(typedKey.toString(), form[typedKey].toString());
          }
        }
      }
      await WorkshopApi.updateWorkshop(formData).then((data) => {
        if (data.code == "201") {
          // Assuming 200 for successful update
          alert.success(data.message);
          setSuccess('Workshop updated successfully!');
        } else {
          alert.error(data.message);
        }
      })
      .catch((err) => {
        alert.error(err.message || "Failed to update workshop.");
      })
      .finally(() => {
        
      });
      
    } catch (e) {
      setError('Failed to update workshop');
    } finally {
      setSaving(false);
    }
  };

  // File upload handler
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(event.target.files || []);
  
    // Tạo danh sách file mới kết hợp file cũ
    setForm((prev: any) => ({
      ...prev,
      attached_documents: [...prev.attached_documents, ...newFiles]
    }));
  };
  
  // Remove file
  const handleRemoveFile = (index: number) => {
    setForm((prev: any) => ({
      ...prev,
      attached_documents: prev.attached_documents.filter((_: any, i: any) => i !== index)
    }));
  };
  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  return (
    <Card sx={{ maxWidth: 1000, mx: 'auto', mt: 2, px: 2 }}>
      <CardContent>
        <Typography variant="h5" fontWeight={600} gutterBottom>Workshop Information</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={8}>
            <TextField label="Title" name="title" value={form.title || ''} onChange={handleChange} fullWidth margin="normal" required sx={{
                    // Truy cập vào lớp CSS của dấu hoa thị
                    '& .MuiInputLabel-asterisk': {
                      color: 'red', // Đổi màu dấu hoa thị thành đỏ
                    },
                  }} />
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Avatar src={form.image_url} sx={{ width: 80, height: 80 }} variant="rounded" />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              name="description"
              value={form.description || ''}
              onChange={handleChange}
              fullWidth
              margin="normal"
              multiline
              sx={{
                // Truy cập vào lớp CSS của dấu hoa thị
                '& .MuiInputLabel-asterisk': {
                  color: 'red', // Đổi màu dấu hoa thị thành đỏ
                },
              }}
              minRows={5}
              maxRows={12}
              InputProps={{ style: { whiteSpace: 'pre-line' } }}
              placeholder="Enter a detailed description for the workshop..."
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Learning Objectives"
              name="learning_objectives"
              value={form.learning_objectives || ''}
              onChange={handleChange}
              fullWidth
              required
              sx={{
                // Truy cập vào lớp CSS của dấu hoa thị
                '& .MuiInputLabel-asterisk': {
                  color: 'red', // Đổi màu dấu hoa thị thành đỏ
                },
              }}
              margin="normal"
              multiline
              minRows={5}
              maxRows={12}
              InputProps={{ style: { whiteSpace: 'pre-line' } }}
              placeholder="List the learning objectives, one per line..."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Target Audience" required name="target_audience" value={form.target_audience || ''} onChange={handleChange} fullWidth margin="normal" multiline rows={2} sx={{
                    // Truy cập vào lớp CSS của dấu hoa thị
                    '& .MuiInputLabel-asterisk': {
                      color: 'red', // Đổi màu dấu hoa thị thành đỏ
                    },
                  }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Date" name="date" required type="datetime-local" value={form.date} onChange={handleChange} fullWidth margin="normal" InputLabelProps={{ shrink: true }} sx={{
                    // Truy cập vào lớp CSS của dấu hoa thị
                    '& .MuiInputLabel-asterisk': {
                      color: 'red', // Đổi màu dấu hoa thị thành đỏ
                    },
                  }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Duration (minutes)" required name="duration" type="number" value={form.duration || ''} onChange={handleChange} fullWidth margin="normal" sx={{
                    // Truy cập vào lớp CSS của dấu hoa thị
                    '& .MuiInputLabel-asterisk': {
                      color: 'red', // Đổi màu dấu hoa thị thành đỏ
                    },
                  }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Format <span style={{color: "red"}}>*</span></InputLabel>
              <Select name="format" required value={form.format || ''} label="Format" onChange={handleSelect}>
                {formats.map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            {/* <Autocomplete
              options={Array.isArray(categories) ? categories : []}
              getOptionLabel={(option) => option.name || ''}
              value={Array.isArray(categories) ? categories.find(c => c.id === form.category) || null : null}
              onChange={handleCategory}
              renderInput={(params) => <TextField {...params} label="Category" margin="normal" required />}
            /> */}
            <FormControl fullWidth margin="normal">
              <InputLabel>Category <span style={{color: "red"}}>*</span></InputLabel>
              <Select
                defaultValue="ai"
                label="Category"
                required
                value={form.category}
                onChange={(e) =>
                  setForm((prev: any) => ({
                    ...prev,
                    category: e.target.value,
                  }))
                }
              >
                {categories.map((category, index) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Max Attendee" name="max_attendee" type="number" value={form.max_attendee || ''} required onChange={handleChange} fullWidth margin="normal" sx={{
                    // Truy cập vào lớp CSS của dấu hoa thị
                    '& .MuiInputLabel-asterisk': {
                      color: 'red', // Đổi màu dấu hoa thị thành đỏ
                    },
                  }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Event Link" name="event_link" value={form.event_link || ''} onChange={handleChange} fullWidth margin="normal" sx={{
                    // Truy cập vào lớp CSS của dấu hoa thị
                    '& .MuiInputLabel-asterisk': {
                      color: 'red', // Đổi màu dấu hoa thị thành đỏ
                    },
                  }}/>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="normal"
              label="Video Link"
              type="text"
              value={form.video_recording_url}
              onChange={(e) =>
                setForm((prev: any) => ({
                  ...prev,
                  video_recording_url: e.target.value,
                }))
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                // Tên trường luôn cố định là 'status'
                name="status"
                // Giá trị hiển thị sẽ là calculatedStatus nếu có, nếu không thì là status
                value={form.calculatedStatus || form.status || ''}
                label="Status"
                onChange={handleSelect}
                required
              >
                {statusOptions.map(s => (
                  <MenuItem key={s.value} value={s.value} disabled={s.value === 'completed' || s.value === 'in_progress'}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              multiple
              freeSolo
              options={topics}
              getOptionLabel={(option: any) => option.name}
              value={form.topic} // Thay thế selectedTopics bằng formData.topics
              isOptionEqualToValue={(option, value) =>
                option.id == value.id
              }
              onChange={handleTopicsChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={
                    <>
                      Workshop Tags <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  placeholder="Type a tag and press Enter"
                  // Thêm helperText ở đây để hướng dẫn người dùng
                  helperText="Add tags to help educators discover your workshop"
                  sx={{
                    '& .MuiFormHelperText-root': {
                      // fontWeight: 'bold', // Ví dụ: in đậm chữ
                      fontSize: '0.9rem', // Ví dụ: tăng kích thước chữ
                      // Bạn có thể thêm các thuộc tính CSS khác tùy ý
                    },
                  }}
                />
              )}
              renderTags={(topicValue, getTagProps) => 
                topicValue && topicValue.map((option, index) => (
                  <Chip label={option.name} {...getTagProps({ index })} />
                ))
              }
            />
          </Grid>
        </Grid>

        <CardContent sx={{ borderBottom: 1, borderColor: "divider", p: 0, mb: 2 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
          >
            <Typography variant="h4" fontWeight={500}>
              Host Details
            </Typography>
          </Box>
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <RadioGroup
              row
              value={form.host_type}
              onChange={(e) =>
                setForm((prev: any) => ({
                  ...prev,
                  host_type: e.target.value,
                }))
              }
            >
              <FormControlLabel
                value="pythaverse"
                control={<Radio />}
                label="🏢 Pythaverse Educator"
              />
              <FormControlLabel
                value="guest"
                control={<Radio />}
                label="🎤 Invited Guest"
              />
            </RadioGroup>
          </FormControl>
          {form.host_type == "pythaverse" ? (
            <Autocomplete
              multiple
              fullWidth
              options={educators}
              getOptionLabel={(option) => option.display_name + ' (' + option.user_email + ')'} // Display educator's name
              filterOptions={(options, state) => {
                // Lọc các lựa chọn để loại bỏ những người đã được chọn
                const filteredOptions = options.filter(
                  (option) =>
                    !selectedEducators.some(
                      (selected) => selected.user_email === option.user_email
                    )
                );
                return filteredOptions;
              }}
              onInputChange={(event, newInputValue) => {
                setSearchEducators(newInputValue); // Update search term to trigger debounced search
                
              }}
              onChange={(event, newValue) => {
                setSelectedEducators(newValue); // Set the selected educator object
              }}
              value={selectedEducators} // Controlled component: display the selected educator
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search and add Educators"
                  placeholder="Search and add Educators..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {debouncedSearchTerm &&
                        educators.length === 0 &&
                        !selectedEducators ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              // Optional: Render a custom option
              renderOption={(props, option) => (
                <li {...props}>
                  <ListItemText
                    primary={option.display_name}
                    secondary={option.user_email}
                  />
                </li>
              )}
              noOptionsText={
                searchEducators
                  ? "No educators found."
                  : "Start typing to Search and add Educatorss."
              }
            />
          ) : (
            <Box>
              {form.hosts.map((host: any, index: any) => (
                  <Grid container spacing={2} key={index} alignItems="center" sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={10}>
                      <Grid container spacing={2}>
                          <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Guest's Full Name"
                                value={host.host_name || ''}
                                onChange={(e) => {
                                  const newHosts = [...form.hosts];
                                  newHosts[index].host_name = e.target.value;
                                  setForm({ ...form, hosts: newHosts });
                                }}
                              />
                          </Grid>
                          <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Guest's Email"
                                value={host.host_email || ''}
                                onChange={(e) => {
                                  const newHosts = [...form.hosts];
                                  newHosts[index].host_email = e.target.value;
                                  setForm({ ...form, hosts: newHosts });
                                }}
                              />
                          </Grid>
                          <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Guest's Title & Organization"
                                value={host.host_organization || ''}
                                onChange={(e) => {
                                  const newHosts = [...form.hosts];
                                  newHosts[index].host_organization = e.target.value;
                                  setForm({ ...form, hosts: newHosts });
                                }}
                              />
                          </Grid>
                          <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Guest's Professional Bio"
                                multiline
                                rows={3}
                                value={host.host_bio || ''}
                                onChange={(e) => {
                                  const newHosts = [...form.hosts];
                                  newHosts[index].host_bio = e.target.value;
                                  setForm({ ...form, hosts: newHosts });
                                }}
                              />
                          </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <IconButton onClick={() => handleRemoveGuest(index)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
              ))}
              <Button onClick={handleAddGuest} variant="contained" startIcon={<AddIcon />}>
                Add Guest
              </Button>
            </Box>
          )}
        </CardContent>
        {/* Attached Documents */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h5" fontWeight={500}>
              Resources
            </Typography>
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <input
              accept="image/*,application/pdf,.doc,.docx,.zip"
              style={{ display: 'none' }}
              id="upload-documents"
              type="file"
              multiple
              onChange={handleFileUpload}
            />
            <label htmlFor="upload-documents">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUpload />}
              >
                Upload documents
              </Button>
            </label>
          </Box>
          {form.attached_documents && form.attached_documents.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Uploaded files:
              </Typography>
              {form.attached_documents.map((file: any, index: any) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    mb: 1
                  }}
                >
                  <Typography variant="body2">{file instanceof File ? file.name : file.file_name}</Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveFile(index)}
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

        </Box>
        {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        <Button variant="contained" color="primary" sx={{ mt: 3, minWidth: 120 }} onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </CardContent>
    </Card>
  );
}

function WorkshopAttendees({ workshopId }: { workshopId: string | undefined }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<any[]>([]);

  const fetchData = () => {
    if (!workshopId) return;
    setLoading(true);
    setError(null);
    WorkshopAdminApi.getRegisterByWorkshop(Number(workshopId))
      .then((res: any) => {
        if (res && res.data) setRows(res.data);
        else setRows([]);
      })
      .catch(() => setError('Failed to fetch attendees'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [workshopId]);

  const columns: GridColDef[] = [
    { field: 'user_fullname', headerName: 'Name', flex: 1 },
    { field: 'user_email', headerName: 'Email', flex: 1 },
    { field: 'username', headerName: 'Username', flex: 1 },
    { field: 'created_at', headerName: 'Registration Date', flex: 1, valueGetter: (params) => {
      const date = new Date(params.value);

      // Sử dụng các phương thức getUTC... để lấy các thành phần theo giờ UTC
      const year = date.getUTCFullYear();
      const month = (date.getUTCMonth() + 1).toString().padStart(2, '0'); // Tháng bắt đầu từ 0
      const day = date.getUTCDate().toString().padStart(2, '0');
      const hours = date.getUTCHours().toString().padStart(2, '0');
      const minutes = date.getUTCMinutes().toString().padStart(2, '0');
      const seconds = date.getUTCSeconds().toString().padStart(2, '0');

      // Trả về chuỗi định dạng yyyy-mm-dd H:m:s
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } },
  ];

  const handleMenuClick = (index: number) => {
    if (index === 0) fetchData();
    if (index === 1) exportCsv();
  };

  const exportCsv = () => {
    if (!rows.length) return;
    const header = ['Name', 'Email', 'Username', 'Registration Date'];
    const csvRows = rows.map(row => [
      row.user_fullname,
      row.user_email,
      row.username,
      new Date(row.created_at).toLocaleString()
    ]);
    const csvContent = [header, ...csvRows].map(e => e.map(x => `"${x ?? ''}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'attendees.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) return <CircularProgress sx={{ mt: 2 }} />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!rows.length) return <Alert severity="info">No attendees found.</Alert>;

  return (
    <Box sx={{ mx: 'auto', mt: 2, px: 2 }}>
      <DttTableData
        columns={columns}
        rows={rows}
        loading={loading}
        buttonOptions={[{ label: 'Reload Data' }, { label: 'Export CSV' }]}
        onMenuClick={handleMenuClick}
        checkboxSelection={false}
        disableRowSelectionOnClick={true}
        hideSearchBox={false}
      />
    </Box>
  );
}

function WorkshopBulkEmail({ workshopId }: { workshopId: string | undefined }) {
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<any[]>([]);

  // TODO: fetch history from API if available
  // useEffect(() => { ... }, [workshopId]);

  const handleSend = async () => {
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      // TODO: call real API
      await WorkshopAdminApi.sendBulkEmail(Number(workshopId), subject, content);
      setSuccess('Email sent to all attendees!');
      setHistory([{ subject, content, date: new Date().toLocaleString() }, ...history]);
      setSubject('');
      setContent('');
    } catch (e) {
      setError('Failed to send email.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 2, px: 2 }}>
      <Typography variant="h6" gutterBottom>Bulk Email to Attendees</Typography>
      <TextField
        label="Subject"
        value={subject}
        onChange={e => setSubject(e.target.value)}
        fullWidth
        margin="normal"
      />
      <Box sx={{ mt: 2, mb: 2 }}>
        <TextEditor content={content} onChange={setContent} />
      </Box>
      {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      <Button
        variant="contained"
        color="primary"
        sx={{ mt: 2, minWidth: 120 }}
        onClick={handleSend}
        disabled={sending || !subject.trim() || !content.trim()}
      >
        {sending ? <CircularProgress size={20} /> : 'Send Email'}
      </Button>
      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Email History</Typography>
        {history.length === 0 ? (
          <Typography color="text.secondary">No email sent yet.</Typography>
        ) : (
          <Box component="ul" sx={{ pl: 2 }}>
            {history.map((item, idx) => (
              <li key={idx}>
                <strong>{item.subject}</strong> ({item.date})<br />
                <span style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{__html: item.content}} />
              </li>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function WorkshopAnalytics({ workshopId }: { workshopId: string | undefined }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<any>({
    registration_count: 0,
    max_attendee: 0,
    attendanceRate: 0,
    registration_trend: [],
    view_count: 0,
  });

  const fetchData = () => {
    if (!workshopId) return;
    setLoading(true);
    setError(null);
    WorkshopAdminApi.getWorkshopAnalyticsById(Number(workshopId))
      .then((response: any) => {
        if (response.status === 'success' && response.data) {
          const { workshop_id, title, registration_count, max_attendee, view_count, registration_trend } = response.data;
          setAnalytics({
            ...analytics,
            workshop_id, // Update state with workshop_id
            title, // Update state with title
            registration_count,
            max_attendee,
            view_count,
            // Sort data by date to ensure the chart displays chronologically
            registration_trend: registration_trend.sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()),
          });
        } else {
          setError('Invalid data format from API');
        }
      })
      .catch(() => setError('Failed to fetch attendees'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, [workshopId]);

  const handleExportAnalytics = () => {
    console.log(analytics)
    if (!analytics) {
      alert('No analytics data to export.');
      return;
    }

    // Main data for export
    const mainData = [
      ['Metric', 'Value'],
      ['Workshop ID', analytics.workshop_id || 'N/A'],
      ['Workshop Title', analytics.title || 'N/A'],
      ['Total Registrations', analytics.registration_count],
      ['Max Attendee Capacity', analytics.max_attendee],
      ['Page Views', analytics.view_count],
      // You can add Attendance Rate if actual data is available
      // ['Attendance Rate', `${analytics.attendanceRate}%`],
    ];

    // Registration trend data
    let trendData = [['Date', 'Registrations']];
    if (analytics.registration_trend && analytics.registration_trend.length > 0) {
      trendData = trendData.concat(analytics.registration_trend.map((item: any) => [item.date, item.count]));
    }

    // Combine all data into a CSV string
    const mainCsv = mainData.map(row => row.join(',')).join('\n');
    const trendCsv = trendData.map(row => row.join(',')).join('\n');

    // Create CSV file content
    const csvContent = `Workshop Analytics Report\n\n${mainCsv}\n\nRegistration Trend\n${trendCsv}`;

    // Create a Blob from the CSV content
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    // Create an object URL and a download link
    const link = document.createElement('a');
    if (link.download !== undefined) { // Check for download attribute support
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `workshop_analytics_${analytics.workshop_id || 'report'}.csv`);
      link.style.visibility = 'hidden'; // Hide the link
      document.body.appendChild(link); // Add to body
      link.click(); // Simulate click to trigger download
      document.body.removeChild(link); // Remove from body after download
      URL.revokeObjectURL(url); // Revoke the object URL
    } else {
      // Fallback for browsers that don't support the download attribute (rare nowadays)
      alert('Your browser does not support direct download. Please try another method.');
    }
  };

  if (loading) return <CircularProgress sx={{ mt: 2 }} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 2, px: 2 }}>
      <Typography variant="h6" gutterBottom>Workshop Analytics</Typography>
      
      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" gutterBottom>
                Total Registrations
              </Typography>
              <Typography variant="h4" component="div" color="primary">
                {analytics.registration_count}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                of {analytics.max_attendee} capacity
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" gutterBottom>
                Attendance Rate
              </Typography>
              <Typography variant="h4" component="div" color="success.main">
                {analytics.attendanceRate}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Post-event metric
              </Typography>
            </CardContent>
          </Card>
        </Grid> */}
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" gutterBottom>
                Page Views
              </Typography>
              <Typography variant="h4" component="div" color="info.main">
                {analytics.view_count}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total views
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Registration Trend Chart */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Registration Trend</Typography>
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50' }}>
            {analytics.registration_trend && analytics.registration_trend.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={analytics.registration_trend}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="count" stroke="#8884d8" activeDot={{ r: 8 }} name="Registrations" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary">
                No data available
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Additional Metrics */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Quick Actions</Typography>
              <Button
                variant="outlined"
                fullWidth
                sx={{ mb: 2 }}
                onClick={handleExportAnalytics}
              >
                Export Analytics Report
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

function WorkshopCancel({ workshopId, setWorkshopStatus }: { workshopId: string | undefined, setWorkshopStatus: (status: string) => void }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [reason, setReason] = useState('');
  const [workshopInfo, setWorkshopInfo] = useState<any>(null);

  useEffect(() => {
    if (!workshopId) return;
    // Fetch workshop info for confirmation
    WorkshopAdminApi.adminGetWorkshopById(workshopId)
      .then((res: any) => {
        if (res && res.data) {
          setWorkshopInfo(res.data);
        }
      })
      .catch(() => setError('Failed to fetch workshop info'));
  }, [workshopId]);

  const handleCancelWorkshop = async () => {
    if (!reason.trim()) {
      setError('Please provide a reason for cancellation');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await WorkshopAdminApi.cancelWorkshop(workshopId as string, reason);
      setSuccess('Workshop cancelled successfully. All registrants have been notified.');
      setOpenDialog(false);
      setReason('');
      // Fetch lại workshop để cập nhật status
      const res = await WorkshopAdminApi.adminGetWorkshopById(workshopId);
      if (res && res.data) {
        setWorkshopStatus(res.data.status || 'cancelled');
      }
    } catch (e) {
      setError('Failed to cancel workshop');
    } finally {
      setLoading(false);
    }
  };

  const reasons = [
    'Insufficient registrations',
    'Host unavailable',
    'Technical issues',
    'Venue/location issues',
    'Schedule conflict',
    'Other',
  ];

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 2, px: 2 }}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom color="error">
            Cancel Workshop
          </Typography>
          
          {workshopInfo && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Workshop:</strong> {workshopInfo.title}
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {new Date(workshopInfo.date).toLocaleDateString()}
              </Typography>
              <Typography variant="body2">
                <strong>Current Registrations:</strong> {workshopInfo.registrations || 0}
              </Typography>
            </Alert>
          )}

          <Typography variant="body1" sx={{ mb: 3 }}>
            Cancelling this workshop will:
          </Typography>
          
          <Box component="ul" sx={{ pl: 3, mb: 3 }}>
            <li>Immediately close registration</li>
            <li>Send cancellation emails to all registered attendees</li>
            <li>Remove the workshop from public listings</li>
            <li>Mark the workshop as cancelled in the system</li>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            <strong>Note:</strong> This action cannot be undone. Please ensure this is the correct decision.
          </Typography>

          <Button
            variant="contained"
            color="error"
            size="large"
            startIcon={<Cancel />}
            onClick={() => setOpenDialog(true)}
            sx={{ minWidth: 200 }}
          >
            Cancel Workshop
          </Button>

          {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Workshop Cancellation</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to cancel this workshop? This action will notify all registered attendees.
          </Typography>
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <MuiInputLabel>Reason for Cancellation</MuiInputLabel>
            <Select
              value={reason}
              label="Reason for Cancellation"
              onChange={(e) => setReason(e.target.value)}
              required
            >
              {reasons.map((r) => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {reason === 'Other' && (
            <TextField
              label="Please specify"
              fullWidth
              multiline
              rows={3}
              sx={{ mt: 2 }}
              placeholder="Please provide a detailed reason for cancellation..."
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={loading}>
            Keep Workshop
          </Button>
          <Button
            onClick={handleCancelWorkshop}
            color="error"
            variant="contained"
            disabled={loading || !reason.trim()}
          >
            {loading ? <CircularProgress size={20} /> : 'Confirm Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function WorkshopAdminDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = React.useState(0);
  const [workshopStatus, setWorkshopStatus] = useState<string>('');

  // Fetch workshop status for tab disabling
  useEffect(() => {
    if (!id) return;
    WorkshopAdminApi.adminGetWorkshopById(id)
      .then((res: any) => {
        if (res && res.data) {
          const calculatedStatus = calculateWorkshopStatus(res.data);
          setWorkshopStatus(calculatedStatus);
        }
      })
      .catch(() => {
        // If failed to fetch, assume published status
        setWorkshopStatus(WORKSHOP_STATUS_PUBLISHED);
      });
  }, [id]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (!isTabDisabled(newValue, workshopStatus)) {
      setTab(newValue);
    }
  };

  return (
    <Box component="main" sx={{ flexGrow: 1, bgcolor: '#f5f6fa', minHeight: '100vh', p: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/workshop-management/workshops", { state: { activeTab: 1 } })}
        variant="contained"
        color="primary"
        sx={{ 
          mb: 3,
          px: 3,
          py: 1.5,
          fontSize: '1rem',
          fontWeight: 600,
          boxShadow: 2,
          '&:hover': {
            boxShadow: 4,
            transform: 'translateY(-1px)'
          }
        }}
      >
        Back to All Workshops
      </Button>
      
      <Typography variant="h4" fontWeight={600} gutterBottom>
        Workshop Management #{id}
      </Typography>
      <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 3 }}>
        {tabLabels.map((label, idx) => (
          <Tab 
            key={idx} 
            label={label} 
            disabled={isTabDisabled(idx, workshopStatus)}
            sx={{
              opacity: isTabDisabled(idx, workshopStatus) ? 0.5 : 1,
              '&.Mui-disabled': {
                color: 'text.disabled',
              }
            }}
          />
        ))}
      </Tabs>
      <Box>
        {tab === 0 && <WorkshopInfoEdit workshopId={id} />}
        {tab === 1 && <WorkshopAttendees workshopId={id} />}
        {tab === 2 && <WorkshopBulkEmail workshopId={id} />}
        {tab === 3 && <WorkshopAnalytics workshopId={id} />}
        {tab === 4 && <WorkshopCancel workshopId={id} setWorkshopStatus={setWorkshopStatus} />}
      </Box>
    </Box>
  );
} 